import { computed, ref } from 'vue'
import { dateTimeFieldConfig } from './dateFieldPresets'
import { api } from '@/global/services/api'
import { isFunction, isObject } from '@/global/utils/utils'

const TRANSLATION_NAMESPACE = 'road-maintenance/temporary_regulation/work_order_details'

const phaseTypeItems = ref([])
let phaseTypeItemsRequested = false

function fetchPhaseTypeItems () {
  if (phaseTypeItemsRequested) {
    return
  }
  phaseTypeItemsRequested = true

  api('road-maintenance').get('codebook', { types: ['PROJECT_PHASE_TYPE'] })
    .then(({ data }) => {
      phaseTypeItems.value = data?.project_phase_type || []
    })
    .catch((exception) => {
      console.error(exception)
      phaseTypeItemsRequested = false
    })
}

const COLUMNS_COUNT = 4

export function usePhaseFields (options) {
  const { translate, phase: getPhase } = options || {}

  fetchPhaseTypeItems()

  const fields = computed(() => {
    // Called (not just read) inside this computed so it's evaluated fresh on
    // every recompute, tracking whichever phase object is CURRENTLY assigned
    // to the prop - a plain reference captured once at setup time would go
    // stale the moment `formData.phases` gets replaced wholesale (eg. after
    // "refresh data" re-fetches everything), silently freezing `edit` at
    // whatever it was and breaking the fields that should hide while editing.
    const phase = isFunction(getPhase) ? getPhase() : getPhase

    const allFields = [
      {
        key: 'type_id',
        label: translate(`${TRANSLATION_NAMESPACE}.type`),
        type: 'autocomplete',
        items: phaseTypeItems.value,
        autocomplete_options: {
          custom_display_value: (item) => translate(item?.description || ''),
          allowSearch: false
        },
        // Picking a type on a phase that has no name yet names the phase after
        // that type - a name typed by the user is never overwritten.
        onSelect: ({ selectedItem, updateData }) => {
          const currentPhase = isFunction(getPhase) ? getPhase() : getPhase

          if (currentPhase?.name?.trim() || !isFunction(updateData)) {
            return
          }

          const typeId = isObject(selectedItem) ? selectedItem.id : selectedItem
          const selectedType = phaseTypeItems.value.find((item) => Number(item?.id) === Number(typeId))
          const name = selectedType?.description ? translate(selectedType.description) : ''

          if (!name) {
            return
          }

          updateData({ name })
        },
        editable: true,
        visible: true
      },
      {
        key: 'name',
        label: translate(`${TRANSLATION_NAMESPACE}.phase_name`),
        editable: true,
        visible: true
      },
      {
        ...dateTimeFieldConfig('planned_start', translate(`${TRANSLATION_NAMESPACE}.start_time`)),
        editable: true,
        visible: true
      },
      {
        ...dateTimeFieldConfig('planned_end', translate(`${TRANSLATION_NAMESPACE}.end_time`)),
        editable: true,
        visible: true
      },
      {
        key: 'status_label',
        label: translate(`${TRANSLATION_NAMESPACE}.status`),
        editable: false,
        visible: (vm) => !!vm.formData?.id && !vm.edit
      },
      {
        // Read-only display of an already formatted datetime string - a plain
        // text field, not a picker, since there is nothing to pick here.
        key: 'actual_start',
        label: translate(`${TRANSLATION_NAMESPACE}.actual_start`),
        editable: false,
        visible: (vm) => !!vm.formData?.id && !vm.edit
      },
      {
        key: 'actual_end',
        label: translate(`${TRANSLATION_NAMESPACE}.actual_end`),
        editable: false,
        visible: (vm) => !!vm.formData?.id && !vm.edit
      }
    ]

    // FieldsRenderer groups fields by their `row` and pads each row up to
    // `columnsCount` with blank placeholders - fixed row numbers can leave
    // gaps whenever some fields on a row are hidden (eg. entering edit mode).
    // Assign rows dynamically, after filtering to only the currently visible
    // fields, so they always pack 4-per-row with no gaps.
    const evalVm = { formData: phase, edit: !!phase?.editing }

    return allFields
      .filter(field => {
        const { visible } = field
        return typeof visible === 'function' ? visible(evalVm) : !!visible
      })
      .map((field, index) => ({
        ...field,
        row: Math.floor(index / COLUMNS_COUNT),
        visible: true
      }))
  })

  return computed(() => ({
    columnsCount: COLUMNS_COUNT,
    fields: fields.value
  }))
}
